import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Wallet, Calendar as CalIcon, Send, FileSpreadsheet, Download, Plus, Trash2,
  Edit, AlertTriangle, ShieldAlert, Search, Building2, UserCircle2, RefreshCw,
  Lock, Loader2, ChevronDown, ChevronRight, CheckSquare, Square, Upload,
  Receipt, Banknote, Users as UsersIcon, ArrowRight, ArrowLeft, FileText, Copy
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import { esGestorGastos } from '../utils/permissions';

const fmtCLP = (n) => {
  if (n == null || isNaN(n)) return '$ 0';
  return '$ ' + Number(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};

const semaforoColor = {
  rojo: 'bg-red-100 text-red-800 border-red-300',
  amarillo: 'bg-amber-100 text-amber-800 border-amber-300',
  verde: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

export default function GastosRemuneraciones() {
  // ACL check (defense in depth: backend ya valida, esto es UX)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const autorizado = esGestorGastos(currentUser);

  const [metadata, setMetadata] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('matriz');
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [editProveedor, setEditProveedor] = useState(null);
  const [showGastoModal, setShowGastoModal] = useState(false);
  const [editGasto, setEditGasto] = useState(null);
  const [showRemuneracionesModal, setShowRemuneracionesModal] = useState(false);
  const [seleccionados, setSeleccionados] = useState({}); // {id: true}
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(async () => {
    if (!autorizado) return;
    setLoading(true);
    try {
      const [m, d, p, i] = await Promise.all([
        api.get('/gastos/metadata'),
        api.get('/gastos/dashboard'),
        api.get('/gastos/proveedores'),
        api.get('/gastos/items')
      ]);
      setMetadata(m.data);
      setDashboard(d.data);
      setProveedores(p.data.items || []);
      setItems(i.data.items || []);
    } catch (e) {
      toast.error('Error: ' + formatErrorMessage(e));
    } finally { setLoading(false); }
  }, [autorizado]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset selection cuando cambia el tab
  useEffect(() => { setSeleccionados({}); }, [tab]);

  // ============= FILTRADO POR TAB =============
  const itemsPorTab = useMemo(() => {
    let arr = [];
    if (tab === 'matriz' || tab === 'vencimiento') arr = items.filter(i => i.estado === 'matriz');
    else if (tab === 'a-pagar') arr = items.filter(i => i.estado === 'a-pagar');
    else if (tab === 'pagado') arr = items.filter(i => i.estado === 'pagado');
    if (busqueda) {
      const q = busqueda.toLowerCase();
      arr = arr.filter(i =>
        (i.concepto || '').toLowerCase().includes(q) ||
        (i.proveedor_nombre || '').toLowerCase().includes(q) ||
        (i.comprobante || '').toLowerCase().includes(q)
      );
    }
    if (tab === 'vencimiento') {
      arr = [...arr].sort((a, b) => {
        const so = { rojo: 0, amarillo: 1, verde: 2 };
        const aS = so[a.semaforo] ?? 3, bS = so[b.semaforo] ?? 3;
        if (aS !== bS) return aS - bS;
        return (a.fecha_vencimiento || '').localeCompare(b.fecha_vencimiento || '');
      });
    } else {
      arr = [...arr].sort((a, b) => (a.fecha_vencimiento || '').localeCompare(b.fecha_vencimiento || ''));
    }
    return arr;
  }, [items, tab, busqueda]);

  const idsSeleccionados = useMemo(
    () => Object.keys(seleccionados).filter(k => seleccionados[k]),
    [seleccionados]
  );
  const totalSeleccionado = useMemo(
    () => itemsPorTab.filter(i => seleccionados[i.id]).reduce((acc, i) => acc + Number(i.monto_clp || 0), 0),
    [itemsPorTab, seleccionados]
  );

  // ============= ACCESO DENEGADO =============
  if (!autorizado) {
    return (
      <Card className='p-12 text-center border-red-200 bg-red-50' data-testid='gastos-acceso-denegado'>
        <Lock className='w-16 h-16 text-red-400 mx-auto mb-4' />
        <h2 className='text-2xl font-bold text-red-900 mb-2'>Acceso Denegado</h2>
        <p className='text-red-700 max-w-lg mx-auto'>
          Este submódulo contiene información confidencial financiera y de remuneraciones.
          Solo personal autorizado puede acceder.
        </p>
        <p className='text-xs text-red-500 mt-4'>
          Si necesitas acceso, contacta a un Socio de Peritum.
        </p>
      </Card>
    );
  }

  // ============= ACCIONES =============
  const toggleSel = (id) => setSeleccionados(s => ({ ...s, [id]: !s[id] }));
  const toggleSelTodos = () => {
    const todosMarcados = itemsPorTab.every(i => seleccionados[i.id]);
    const next = { ...seleccionados };
    itemsPorTab.forEach(i => { next[i.id] = !todosMarcados; });
    setSeleccionados(next);
  };

  const bulkMover = async (destino) => {
    if (idsSeleccionados.length === 0) { toast.error('Selecciona al menos un gasto'); return; }
    try {
      const { data } = await api.post('/gastos/bulk/mover', { ids: idsSeleccionados, destino });
      toast.success(`${data.movidos} movidos${data.errores?.length ? ` · ${data.errores.length} errores` : ''}`);
      data.errores?.forEach(e => toast.warning(e));
      setSeleccionados({});
      cargar();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  const bulkEliminar = async () => {
    if (idsSeleccionados.length === 0) { toast.error('Selecciona al menos un gasto'); return; }
    if (!window.confirm(`¿Eliminar ${idsSeleccionados.length} gastos? Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.post('/gastos/bulk/eliminar', { ids: idsSeleccionados });
      toast.success(`${data.eliminados} eliminados`);
      setSeleccionados({});
      cargar();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  const exportarItau = async () => {
    if (idsSeleccionados.length === 0) { toast.error('Selecciona al menos un gasto'); return; }
    try {
      const resp = await api.post('/gastos/exportar-itau', { ids: idsSeleccionados }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `itau_export_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Archivo Itaú generado');
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  return (
    <div className='space-y-5' data-testid='gastos-module'>
      {/* HERO COMPACTO */}
      <div className='relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white shadow-lg'>
        <div className='absolute inset-0 opacity-30 pointer-events-none'
          style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(99,102,241,0.4), transparent 50%), radial-gradient(circle at 80% 20%, rgba(16,185,129,0.3), transparent 50%)' }} />
        <div className='relative p-5 flex items-center justify-between flex-wrap gap-4'>
          <div className='flex items-center gap-3'>
            <div className='p-2.5 rounded-lg bg-white/10 backdrop-blur ring-1 ring-white/20'>
              <Receipt className='w-6 h-6 text-white' />
            </div>
            <div>
              <h2 className='text-xl font-bold tracking-tight'>Gastos y Remuneraciones</h2>
              <div className='flex items-center gap-1.5 text-xs text-slate-300 mt-0.5 flex-wrap'>
                <span className='px-1.5 py-0.5 rounded bg-white/10 font-medium'>Matriz</span>
                <ArrowRight className='w-3 h-3' />
                <span className='px-1.5 py-0.5 rounded bg-white/10 font-medium'>Vencimiento</span>
                <ArrowRight className='w-3 h-3' />
                <span className='px-1.5 py-0.5 rounded bg-white/10 font-medium'>A Pagar</span>
                <ArrowRight className='w-3 h-3' />
                <span className='px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 font-medium'>Pagados</span>
                <span className='text-slate-400 ml-2'>· Exportación Banco Itaú</span>
              </div>
            </div>
          </div>
          <Button variant='secondary' size='sm' onClick={cargar} disabled={loading}
            className='bg-white/10 hover:bg-white/20 text-white border-white/20'
            data-testid='gastos-refresh-btn'>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
        </div>
      </div>

      {/* DASHBOARD */}
      <DashboardCards d={dashboard} />

      {/* PIPELINE VISUAL */}
      <Tabs value={tab} onValueChange={setTab} className='w-full'>
        <PipelineTabs current={tab} dashboard={dashboard} />

        {/* Búsqueda compacta */}
        <div className='mt-5 mb-3 flex items-center gap-2 flex-wrap'>
          <div className='relative flex-1 min-w-[260px] max-w-md'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input placeholder='Buscar por concepto, proveedor, comprobante...'
              value={busqueda} onChange={e => setBusqueda(e.target.value)}
              className='pl-9 h-9 bg-white border-slate-200' data-testid='gastos-search-input' />
          </div>
          {busqueda && (
            <Badge variant='outline' className='text-xs'>
              {itemsPorTab.length} resultado{itemsPorTab.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* MATRIZ */}
        <TabsContent value='matriz'>
          <MatrizPanel
            items={itemsPorTab}
            proveedores={proveedores}
            metadata={metadata}
            onCrearProveedor={() => { setEditProveedor(null); setShowProveedorModal(true); }}
            onEditarProveedor={(p) => { setEditProveedor(p); setShowProveedorModal(true); }}
            onCrearGasto={() => { setEditGasto(null); setShowGastoModal(true); }}
            onEditarGasto={(g) => { setEditGasto(g); setShowGastoModal(true); }}
            onCrearRemuneraciones={() => setShowRemuneracionesModal(true)}
            onReload={cargar}
          />
        </TabsContent>

        {/* VENCIMIENTO */}
        <TabsContent value='vencimiento'>
          <PipelinePanel
            items={itemsPorTab}
            seleccionados={seleccionados}
            toggleSel={toggleSel}
            toggleSelTodos={toggleSelTodos}
            idsSel={idsSeleccionados}
            totalSel={totalSeleccionado}
            tab='vencimiento'
            onBulkMover={bulkMover}
            onBulkEliminar={bulkEliminar}
            onEditar={(g) => { setEditGasto(g); setShowGastoModal(true); }}
            onReload={cargar}
            showSemaforo
          />
        </TabsContent>

        {/* A PAGAR */}
        <TabsContent value='a-pagar'>
          <PipelinePanel
            items={itemsPorTab}
            seleccionados={seleccionados}
            toggleSel={toggleSel}
            toggleSelTodos={toggleSelTodos}
            idsSel={idsSeleccionados}
            totalSel={totalSeleccionado}
            tab='a-pagar'
            onBulkMover={bulkMover}
            onBulkEliminar={bulkEliminar}
            onExportItau={exportarItau}
            onEditar={(g) => { setEditGasto(g); setShowGastoModal(true); }}
            onReload={cargar}
          />
        </TabsContent>

        {/* PAGADOS */}
        <TabsContent value='pagado'>
          <PagadosPanel items={itemsPorTab} onReload={cargar} />
        </TabsContent>
      </Tabs>

      {/* MODALES */}
      {showProveedorModal && metadata && (
        <ProveedorModal
          proveedor={editProveedor}
          metadata={metadata}
          onClose={() => setShowProveedorModal(false)}
          onSaved={() => { setShowProveedorModal(false); cargar(); }}
        />
      )}
      {showGastoModal && metadata && (
        <GastoModal
          gasto={editGasto}
          proveedores={proveedores}
          metadata={metadata}
          onClose={() => setShowGastoModal(false)}
          onSaved={() => { setShowGastoModal(false); cargar(); }}
        />
      )}
      {showRemuneracionesModal && metadata && (
        <RemuneracionesModal
          proveedores={proveedores}
          metadata={metadata}
          onClose={() => setShowRemuneracionesModal(false)}
          onSaved={() => { setShowRemuneracionesModal(false); cargar(); }}
        />
      )}
    </div>
  );
}

// ============= DASHBOARD =============
function DashboardCards({ d }) {
  if (!d) return null;
  const cards = [
    {
      label: 'En Matriz', value: d.matriz,
      sub: d.vencidos_o_hoy > 0 ? `${d.vencidos_o_hoy} vencidos/hoy` : 'Sin vencidos',
      icon: <FileSpreadsheet className='w-5 h-5' />,
      iconBg: 'bg-slate-100 text-slate-700',
      ring: 'ring-slate-200',
      accent: d.vencidos_o_hoy > 0 ? 'text-red-600' : 'text-slate-400',
    },
    {
      label: 'A Pagar', value: d.a_pagar,
      sub: fmtCLP(d.total_a_pagar_clp),
      icon: <Banknote className='w-5 h-5' />,
      iconBg: 'bg-amber-100 text-amber-700',
      ring: 'ring-amber-200',
      accent: 'text-amber-700 font-semibold',
    },
    {
      label: 'Pagados', value: d.pagado,
      sub: 'Histórico inmutable',
      icon: <Receipt className='w-5 h-5' />,
      iconBg: 'bg-emerald-100 text-emerald-700',
      ring: 'ring-emerald-200',
      accent: 'text-emerald-700',
    },
    {
      label: 'Vencidos / Hoy', value: d.vencidos_o_hoy,
      sub: d.vencidos_o_hoy > 0 ? 'Requieren atención' : 'Todo al día',
      icon: <AlertTriangle className='w-5 h-5' />,
      iconBg: d.vencidos_o_hoy > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400',
      ring: d.vencidos_o_hoy > 0 ? 'ring-red-200' : 'ring-slate-200',
      accent: d.vencidos_o_hoy > 0 ? 'text-red-600 font-semibold' : 'text-slate-400',
    },
  ];
  return (
    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
      {cards.map((c, i) => (
        <Card key={i}
          className={`relative p-4 border-0 ring-1 ${c.ring} bg-white hover:shadow-md transition-shadow group`}>
          <div className='flex items-start justify-between mb-3'>
            <div className={`p-2 rounded-lg ${c.iconBg} transition-transform group-hover:scale-105`}>
              {c.icon}
            </div>
          </div>
          <p className='text-[11px] uppercase tracking-wide text-slate-500 font-semibold'>{c.label}</p>
          <p className='text-3xl font-bold text-slate-900 mt-0.5 tabular-nums'>{c.value}</p>
          <p className={`text-xs mt-1 ${c.accent}`}>{c.sub}</p>
        </Card>
      ))}
    </div>
  );
}

// ============= PIPELINE VISUAL TABS =============
function PipelineTabs({ current, dashboard }) {
  const steps = [
    { id: 'matriz', label: 'Matriz', icon: FileSpreadsheet, count: dashboard?.matriz, color: 'slate', desc: 'Cargar' },
    { id: 'vencimiento', label: 'Vencimiento', icon: CalIcon, count: dashboard?.matriz, color: 'amber', desc: 'Priorizar' },
    { id: 'a-pagar', label: 'A Pagar', icon: Banknote, count: dashboard?.a_pagar, color: 'indigo', desc: 'Tesorería' },
    { id: 'pagado', label: 'Pagados', icon: Receipt, count: dashboard?.pagado, color: 'emerald', desc: 'Histórico' },
  ];
  const colorClasses = {
    slate: { active: 'bg-slate-900 text-white shadow-md', inactive: 'bg-white text-slate-600 hover:bg-slate-50' },
    amber: { active: 'bg-amber-500 text-white shadow-md', inactive: 'bg-white text-slate-600 hover:bg-amber-50' },
    indigo: { active: 'bg-indigo-600 text-white shadow-md', inactive: 'bg-white text-slate-600 hover:bg-indigo-50' },
    emerald: { active: 'bg-emerald-600 text-white shadow-md', inactive: 'bg-white text-slate-600 hover:bg-emerald-50' },
  };
  return (
    <TabsList className='w-full h-auto p-1.5 bg-slate-100 rounded-xl border border-slate-200 flex gap-1.5'>
      {steps.map((s, idx) => {
        const Icon = s.icon;
        const isActive = current === s.id;
        const cls = colorClasses[s.color][isActive ? 'active' : 'inactive'];
        return (
          <React.Fragment key={s.id}>
            <TabsTrigger value={s.id}
              data-testid={`gastos-tab-${s.id === 'a-pagar' ? 'apagar' : s.id === 'pagado' ? 'pagados' : s.id}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 px-3 rounded-lg transition-all border border-transparent ${cls} ${isActive ? '' : 'border-slate-200/60'} data-[state=active]:shadow-md`}>
              <div className='flex items-center gap-2'>
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white/20' : 'bg-slate-200 text-slate-600'}`}>
                  {idx + 1}
                </span>
                <Icon className='w-4 h-4' />
                <span className='font-semibold text-sm'>{s.label}</span>
                {s.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/25' : 'bg-slate-200 text-slate-700'}`}>
                    {s.count}
                  </span>
                )}
              </div>
              <span className={`text-[10px] ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{s.desc}</span>
            </TabsTrigger>
            {idx < steps.length - 1 && (
              <div className='hidden md:flex items-center text-slate-300'>
                <ArrowRight className='w-3.5 h-3.5' />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </TabsList>
  );
}

// ============= PANEL MATRIZ =============
function MatrizPanel({ items, proveedores, metadata, onCrearProveedor, onEditarProveedor,
                      onCrearGasto, onEditarGasto, onCrearRemuneraciones, onReload }) {
  const [tabInterno, setTabInterno] = useState('gastos');
  const [filtroTrab, setFiltroTrab] = useState('todos');
  const [creditos, setCreditos] = useState([]);
  const [showCreditoModal, setShowCreditoModal] = useState(false);
  const [editCredito, setEditCredito] = useState(null);

  const cargarCreditos = useCallback(async () => {
    try {
      const { data } = await api.get('/gastos/creditos');
      setCreditos(data.items || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { cargarCreditos(); }, [cargarCreditos]);

  const proveedoresFiltrados = useMemo(() => {
    if (filtroTrab === 'trabajadores') return proveedores.filter(p => p.es_trabajador);
    if (filtroTrab === 'proveedores') return proveedores.filter(p => !p.es_trabajador);
    return proveedores;
  }, [proveedores, filtroTrab]);
  const nTrab = proveedores.filter(p => p.es_trabajador).length;
  const nCreditosActivos = creditos.filter(c => c.activo).length;

  const eliminarCredito = async (c) => {
    if (!window.confirm(`¿Eliminar el crédito "${c.nombre}"?`)) return;
    try {
      await api.delete(`/gastos/creditos/${c.id}`);
      toast.success('Crédito eliminado');
      cargarCreditos();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };
  const generarCuota = async (c) => {
    if (!window.confirm(`Generar cuota ${c.cuotas_pagadas + 1}/${c.cuotas_totales} de "${c.nombre}" por ${fmtCLP(c.monto_cuota)} con vencimiento ${fmtFecha(c.proxima_fecha)}?`)) return;
    try {
      const { data } = await api.post(`/gastos/creditos/${c.id}/generar-cuota`);
      toast.success(`Cuota generada en Matriz · próxima fecha: ${fmtFecha(data.credito_actualizado.proxima_fecha)}`);
      cargarCreditos();
      onReload();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };
  const eliminarProv = async (p) => {
    if (!window.confirm(`¿Eliminar proveedor "${p.nombre}"?`)) return;
    try {
      await api.delete(`/gastos/proveedores/${p.id}`);
      toast.success('Proveedor eliminado');
      onReload();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };
  const eliminarGasto = async (g) => {
    if (!window.confirm(`¿Eliminar gasto "${g.concepto}"?`)) return;
    try {
      await api.delete(`/gastos/items/${g.id}`);
      toast.success('Gasto eliminado');
      onReload();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };
  return (
    <div className='space-y-4'>
      <Tabs value={tabInterno} onValueChange={setTabInterno}>
        <TabsList className='bg-transparent p-0 gap-2 flex-wrap h-auto'>
          <TabsTrigger value='gastos' data-testid='matriz-sub-gastos'
            className='data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm bg-white border border-slate-200 text-slate-700 hover:bg-blue-50 rounded-full px-4 py-1.5'>
            <FileText className='w-4 h-4 mr-1.5' /> Gastos
            <span className='ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 data-[state=active]:bg-white/25'>
              {items.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value='proveedores' data-testid='matriz-sub-proveedores'
            className='data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm bg-white border border-slate-200 text-slate-700 hover:bg-violet-50 rounded-full px-4 py-1.5'>
            <Building2 className='w-4 h-4 mr-1.5' /> Proveedores
            <span className='ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 data-[state=active]:bg-white/25 data-[state=active]:text-white'>
              {proveedores.length}
            </span>
            {nTrab > 0 && (
              <Badge className='ml-1 text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300'>
                <UserCircle2 className='w-2.5 h-2.5 mr-0.5' /> {nTrab}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='creditos' data-testid='matriz-sub-creditos'
            className='data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-sm bg-white border border-slate-200 text-slate-700 hover:bg-emerald-50 rounded-full px-4 py-1.5'>
            <Banknote className='w-4 h-4 mr-1.5' /> Créditos
            <span className='ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 data-[state=active]:bg-white/25 data-[state=active]:text-white'>
              {nCreditosActivos}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value='gastos' className='mt-4 space-y-3'>
          <div className='flex gap-2 flex-wrap items-center'>
            <Button onClick={onCrearGasto} className='bg-blue-600 hover:bg-blue-700' data-testid='gastos-btn-crear-gasto'>
              <Plus className='w-4 h-4 mr-1' /> Nuevo Gasto
            </Button>
            <Button variant='outline' onClick={onCrearRemuneraciones} data-testid='gastos-btn-crear-remuneraciones'>
              <UsersIcon className='w-4 h-4 mr-1' /> Cargar Remuneraciones
            </Button>
          </div>
          <ItemsTable
            items={items}
            mostrarAcciones
            onEditar={onEditarGasto}
            onEliminar={eliminarGasto}
            showSemaforo={false}
          />
        </TabsContent>

        <TabsContent value='proveedores' className='mt-4 space-y-3'>
          <div className='flex items-center gap-2 flex-wrap'>
            <Button onClick={onCrearProveedor} className='bg-violet-600 hover:bg-violet-700' data-testid='gastos-btn-crear-proveedor'>
              <Plus className='w-4 h-4 mr-1' /> Nuevo Proveedor
            </Button>
            <div className='flex gap-1 ml-auto bg-slate-100 p-1 rounded-lg'>
              {[
                { v: 'todos', label: 'Todos', n: proveedores.length },
                { v: 'trabajadores', label: 'Trabajadores', n: nTrab },
                { v: 'proveedores', label: 'Externos', n: proveedores.length - nTrab },
              ].map(o => (
                <button key={o.v}
                  onClick={() => setFiltroTrab(o.v)}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${filtroTrab === o.v ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid={`prov-filter-${o.v}`}>
                  {o.label} <span className='text-slate-400 ml-0.5'>({o.n})</span>
                </button>
              ))}
            </div>
          </div>
          <Card className='border-slate-200 overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 text-slate-600 text-xs uppercase'>
                <tr>
                  <th className='text-left px-4 py-3'>Nombre / Razón Social</th>
                  <th className='text-left px-4 py-3'>RUT</th>
                  <th className='text-left px-4 py-3'>Banco</th>
                  <th className='text-left px-4 py-3'>N° Cuenta</th>
                  <th className='text-left px-4 py-3'>Tipo</th>
                  <th className='text-right px-4 py-3'></th>
                </tr>
              </thead>
              <tbody>
                {proveedoresFiltrados.length === 0 && (
                  <tr><td colSpan={6} className='text-center text-slate-400 py-8'>Sin proveedores en este filtro.</td></tr>
                )}
                {proveedoresFiltrados.map(p => (
                  <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${p.es_trabajador ? 'bg-emerald-50/40' : ''}`} data-testid={`prov-row-${p.id}`}>
                    <td className='px-4 py-2'>
                      {p.nombre}
                      {p.es_trabajador && (
                        <Badge className='ml-2 text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300'
                          data-testid={`prov-trabajador-${p.id}`}>
                          <UserCircle2 className='w-3 h-3 mr-0.5' /> Trabajador
                        </Badge>
                      )}
                    </td>
                    <td className='px-4 py-2 text-slate-600 font-mono text-xs'>{p.rut}</td>
                    <td className='px-4 py-2 text-xs text-slate-700'>
                      {p.banco_nombre || '—'}
                      {p.banco_codigo && <span className='text-slate-400 ml-1'>· SBIF {p.banco_codigo}</span>}
                    </td>
                    <td className='px-4 py-2 text-xs font-mono text-slate-600'>{p.numero_cuenta || '—'}</td>
                    <td className='px-4 py-2 text-xs text-slate-600'>{p.tipo_cuenta || '—'}</td>
                    <td className='px-4 py-2 text-right'>
                      <Button size='sm' variant='ghost' onClick={() => onEditarProveedor(p)}>
                        <Edit className='w-3.5 h-3.5' />
                      </Button>
                      <Button size='sm' variant='ghost' onClick={() => eliminarProv(p)}>
                        <Trash2 className='w-3.5 h-3.5 text-red-500' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value='creditos' className='mt-4 space-y-3'>
          <div className='flex items-center gap-2 flex-wrap'>
            <Button onClick={() => { setEditCredito(null); setShowCreditoModal(true); }}
              className='bg-emerald-600 hover:bg-emerald-700'
              data-testid='btn-crear-credito'>
              <Plus className='w-4 h-4 mr-1' /> Nuevo Crédito
            </Button>
            <div className='ml-3 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-900'>
              <Banknote className='w-3.5 h-3.5 inline mr-1' />
              Cuotas mensuales fijas. Job diario 06:00 AM genera la cuota a Matriz automáticamente.
            </div>
          </div>
          <CreditosTable
            creditos={creditos}
            onEditar={(c) => { setEditCredito(c); setShowCreditoModal(true); }}
            onEliminar={eliminarCredito}
            onGenerarCuota={generarCuota}
          />
        </TabsContent>
      </Tabs>

      {showCreditoModal && metadata && (
        <CreditoModal
          credito={editCredito}
          metadata={metadata}
          onClose={() => setShowCreditoModal(false)}
          onSaved={() => { setShowCreditoModal(false); cargarCreditos(); }}
        />
      )}
    </div>
  );
}

// ============= CRÉDITOS - tabla =============
function CreditosTable({ creditos, onEditar, onEliminar, onGenerarCuota }) {
  if (creditos.length === 0) {
    return (
      <Card className='p-12 text-center border-slate-200 border-dashed'>
        <Banknote className='w-12 h-12 text-slate-300 mx-auto mb-3' />
        <p className='text-slate-500'>Sin créditos registrados.</p>
      </Card>
    );
  }
  return (
    <Card className='border-slate-200 overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-slate-50 text-xs uppercase text-slate-600'>
          <tr>
            <th className='text-left px-4 py-3'>Crédito</th>
            <th className='text-left px-4 py-3'>Banco</th>
            <th className='text-left px-4 py-3'>Tipo</th>
            <th className='text-right px-4 py-3'>Monto cuota</th>
            <th className='text-center px-4 py-3'>Cuotas</th>
            <th className='text-right px-4 py-3'>Saldo</th>
            <th className='text-left px-4 py-3'>Próxima</th>
            <th className='text-right px-4 py-3'></th>
          </tr>
        </thead>
        <tbody>
          {creditos.map(c => (
            <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${!c.activo ? 'opacity-50' : ''}`}
              data-testid={`credito-row-${c.id}`}>
              <td className='px-4 py-2'>
                <div className='font-medium text-slate-800'>{c.nombre}</div>
                {!c.activo && <Badge variant='outline' className='text-[10px]'>Pagado</Badge>}
              </td>
              <td className='px-4 py-2 text-xs text-slate-700'>
                {c.banco_nombre}
                <div className='text-[10px] text-slate-400'>SBIF {c.banco_codigo}</div>
              </td>
              <td className='px-4 py-2 text-xs text-slate-600'>{c.tipo || 'Crédito'}</td>
              <td className='px-4 py-2 text-right font-semibold'>{fmtCLP(c.monto_cuota)}</td>
              <td className='px-4 py-2 text-center'>
                <div className='text-xs font-mono'>{c.cuotas_pagadas}/{c.cuotas_totales}</div>
                <div className='w-20 h-1.5 bg-slate-200 rounded-full mx-auto mt-1'>
                  <div className='h-full bg-emerald-500 rounded-full' style={{ width: `${c.progreso_pct}%` }} />
                </div>
              </td>
              <td className='px-4 py-2 text-right font-semibold text-slate-700'>{fmtCLP(c.saldo_clp)}</td>
              <td className='px-4 py-2 text-xs text-slate-600'>{fmtFecha(c.proxima_fecha)}</td>
              <td className='px-4 py-2 text-right whitespace-nowrap'>
                {c.activo && (
                  <Button size='sm' variant='ghost' onClick={() => onGenerarCuota(c)}
                    title='Generar cuota a Matriz' data-testid={`credito-gen-${c.id}`}>
                    <ArrowRight className='w-3.5 h-3.5 text-indigo-600' />
                  </Button>
                )}
                <Button size='sm' variant='ghost' onClick={() => onEditar(c)}>
                  <Edit className='w-3.5 h-3.5' />
                </Button>
                <Button size='sm' variant='ghost' onClick={() => onEliminar(c)}>
                  <Trash2 className='w-3.5 h-3.5 text-red-500' />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ============= CRÉDITOS - modal =============
function CreditoModal({ credito, metadata, onClose, onSaved }) {
  const [nombre, setNombre] = useState(credito?.nombre || '');
  const [bancoCodigo, setBancoCodigo] = useState(credito?.banco_codigo || '');
  const [tipo, setTipo] = useState(credito?.tipo || 'Consumo');
  const [montoCuota, setMontoCuota] = useState(credito?.monto_cuota || '');
  const [cuotasTotales, setCuotasTotales] = useState(credito?.cuotas_totales || '');
  const [cuotasPagadas, setCuotasPagadas] = useState(credito?.cuotas_pagadas || 0);
  const [proximaFecha, setProximaFecha] = useState(credito?.proxima_fecha || '');
  const [diaPago, setDiaPago] = useState(credito?.dia_pago || '');
  const [notas, setNotas] = useState(credito?.notas || '');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (nombre.trim().length < 2) { toast.error('Nombre requerido'); return; }
    if (!bancoCodigo) { toast.error('Banco requerido'); return; }
    const mc = Number(montoCuota);
    if (!mc || mc <= 0) { toast.error('Monto cuota inválido'); return; }
    const ct = parseInt(cuotasTotales);
    if (!ct || ct <= 0) { toast.error('Cuotas totales inválidas'); return; }
    const cp = parseInt(cuotasPagadas) || 0;
    if (cp > ct) { toast.error('Cuotas pagadas no puede ser > totales'); return; }
    if (!proximaFecha) { toast.error('Próxima fecha requerida'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        banco_codigo: bancoCodigo,
        tipo,
        monto_cuota: mc,
        cuotas_totales: ct,
        cuotas_pagadas: cp,
        proxima_fecha: proximaFecha,
        dia_pago: diaPago ? parseInt(diaPago) : null,
        notas: notas.trim()
      };
      if (credito) await api.put(`/gastos/creditos/${credito.id}`, payload);
      else await api.post('/gastos/creditos', payload);
      toast.success('Guardado');
      onSaved();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-xl' data-testid='modal-credito'>
        <DialogHeader>
          <DialogTitle>{credito ? 'Editar Crédito' : 'Nuevo Crédito'}</DialogTitle>
          <DialogDescription>Registra un crédito con cuotas mensuales fijas. Luego puedes generar la cuota mensual a Matriz para pagarla.</DialogDescription>
        </DialogHeader>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div className='md:col-span-2'>
            <Label>Nombre del crédito *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder='Ej. Crédito Auto Toyota, Hipotecario Casa Las Condes'
              data-testid='input-credito-nombre' />
          </div>
          <div>
            <Label>Banco *</Label>
            <Select value={bancoCodigo} onValueChange={setBancoCodigo}>
              <SelectTrigger data-testid='input-credito-banco'><SelectValue placeholder='Selecciona' /></SelectTrigger>
              <SelectContent>
                {metadata.bancos_sbif.map(b => (
                  <SelectItem key={b.codigo} value={b.codigo}>{b.codigo} - {b.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid='input-credito-tipo'><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value='Consumo'>Consumo</SelectItem>
                <SelectItem value='Auto'>Auto</SelectItem>
                <SelectItem value='Hipotecario'>Hipotecario</SelectItem>
                <SelectItem value='Comercial'>Comercial</SelectItem>
                <SelectItem value='Crédito'>Otro / Línea</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Monto cuota CLP *</Label>
            <Input type='number' value={montoCuota} onChange={e => setMontoCuota(e.target.value)}
              data-testid='input-credito-monto' />
          </div>
          <div>
            <Label>Día de pago (1-31)</Label>
            <Input type='number' min='1' max='31' value={diaPago}
              onChange={e => setDiaPago(e.target.value)}
              data-testid='input-credito-dia' />
          </div>
          <div>
            <Label>Cuotas totales *</Label>
            <Input type='number' value={cuotasTotales}
              onChange={e => setCuotasTotales(e.target.value)}
              data-testid='input-credito-cuotas-totales' />
          </div>
          <div>
            <Label>Cuotas ya pagadas</Label>
            <Input type='number' value={cuotasPagadas}
              onChange={e => setCuotasPagadas(e.target.value)}
              data-testid='input-credito-cuotas-pagadas' />
          </div>
          <div className='md:col-span-2'>
            <Label>Próximo vencimiento *</Label>
            <Input type='date' value={proximaFecha}
              onChange={e => setProximaFecha(e.target.value)}
              data-testid='input-credito-prox' />
          </div>
          <div className='md:col-span-2'>
            <Label>Notas</Label>
            <Textarea rows={2} value={notas} onChange={e => setNotas(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid='btn-credito-guardar'>
            {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= PIPELINE PANEL (Vencimiento + A Pagar) =============
function PipelinePanel({ items, seleccionados, toggleSel, toggleSelTodos, idsSel, totalSel,
                         tab, onBulkMover, onBulkEliminar, onExportItau, onEditar, onReload, showSemaforo }) {
  const allSelected = items.length > 0 && items.every(i => seleccionados[i.id]);
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');

  const itemsFiltrados = useMemo(() => {
    if (tab !== 'vencimiento') return items;
    if (!fDesde && !fHasta) return items;
    return items.filter(i => {
      const f = (i.fecha_vencimiento || '').slice(0, 10);
      if (fDesde && f < fDesde) return false;
      if (fHasta && f > fHasta) return false;
      return true;
    });
  }, [items, fDesde, fHasta, tab]);

  return (
    <div className='space-y-3'>
      {/* Filtro de fechas en Vencimiento */}
      {tab === 'vencimiento' && (
        <div className='flex items-center gap-2 flex-wrap p-2 bg-slate-50/60 border border-slate-200 rounded-md'
          data-testid='venc-filtro-fecha'>
          <CalIcon className='w-4 h-4 text-slate-500' />
          <Label className='text-xs text-slate-600'>Desde</Label>
          <Input type='date' value={fDesde} onChange={e => setFDesde(e.target.value)}
            className='h-8 w-40 text-xs' data-testid='venc-fecha-desde' />
          <Label className='text-xs text-slate-600'>Hasta</Label>
          <Input type='date' value={fHasta} onChange={e => setFHasta(e.target.value)}
            className='h-8 w-40 text-xs' data-testid='venc-fecha-hasta' />
          <Button size='sm' variant='ghost' onClick={() => { setFDesde(''); setFHasta(''); }}
            disabled={!fDesde && !fHasta}
            data-testid='venc-fecha-limpiar'>
            Limpiar
          </Button>
          <Badge variant='outline' className='ml-auto text-[10px]'>
            {itemsFiltrados.length} de {items.length}
          </Badge>
          <Button size='sm' variant='outline' onClick={() => {
            const hoy = new Date().toISOString().slice(0, 10);
            const en7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
            setFDesde(hoy); setFHasta(en7);
          }} data-testid='venc-fecha-7d'>
            Próximos 7 días
          </Button>
          <Button size='sm' variant='outline' onClick={() => {
            const hoy = new Date().toISOString().slice(0, 10);
            const en30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
            setFDesde(hoy); setFHasta(en30);
          }} data-testid='venc-fecha-30d'>
            Próximos 30 días
          </Button>
        </div>
      )}

      {/* Toolbar */}
      <div className='flex items-center justify-between flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-md sticky top-0 z-10'>
        <div className='flex items-center gap-2 flex-wrap'>
          <Badge variant='outline' className='text-sm'>
            {idsSel.length} seleccionados
          </Badge>
          {tab === 'a-pagar' && (
            <Badge className='text-sm bg-emerald-100 text-emerald-800 border-emerald-300' data-testid='gastos-totalizador'>
              Total: {fmtCLP(totalSel)}
            </Badge>
          )}
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          {tab === 'vencimiento' && (
            <>
              <Button size='sm' variant='destructive' onClick={onBulkEliminar} disabled={idsSel.length === 0}
                data-testid='venc-btn-eliminar'>
                <Trash2 className='w-3.5 h-3.5 mr-1' /> Eliminar
              </Button>
              <Button size='sm' onClick={() => onBulkMover('a-pagar')} disabled={idsSel.length === 0}
                className='bg-indigo-600 hover:bg-indigo-700' data-testid='venc-btn-pasar-pago'>
                <ArrowRight className='w-3.5 h-3.5 mr-1' /> Pasar a Pago
              </Button>
            </>
          )}
          {tab === 'a-pagar' && (
            <>
              <Button size='sm' variant='outline' onClick={() => onBulkMover('matriz')} disabled={idsSel.length === 0}
                data-testid='apagar-btn-devolver'>
                <ArrowLeft className='w-3.5 h-3.5 mr-1' /> Devolver a Vencimiento
              </Button>
              <Button size='sm' onClick={() => onBulkMover('pagado')} disabled={idsSel.length === 0}
                className='bg-emerald-600 hover:bg-emerald-700' data-testid='apagar-btn-dar-pagado'>
                <Receipt className='w-3.5 h-3.5 mr-1' /> Dar por Pagado
              </Button>
              <Button size='sm' onClick={onExportItau} disabled={idsSel.length === 0}
                className='bg-blue-600 hover:bg-blue-700' data-testid='apagar-btn-export-itau'>
                <Download className='w-3.5 h-3.5 mr-1' /> Exportar Itaú
              </Button>
            </>
          )}
        </div>
      </div>

      <ItemsTable
        items={itemsFiltrados}
        seleccionados={seleccionados}
        toggleSel={toggleSel}
        toggleSelTodos={toggleSelTodos}
        allSelected={allSelected}
        onEditar={onEditar}
        mostrarAcciones
        showCheckbox
        showSemaforo={showSemaforo}
      />
    </div>
  );
}

// ============= PAGADOS PANEL (acordeón por proveedor) =============
function PagadosPanel({ items, onReload }) {
  const [expandidos, setExpandidos] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');
  const toggle = (pid) => setExpandidos(e => ({ ...e, [pid]: !e[pid] }));

  const fechaPago = (i) => ((i.updated_at || i.created_at || '').slice(0, 10));

  const itemsFiltrados = useMemo(() => {
    if (!fDesde && !fHasta) return items;
    return items.filter(i => {
      const fp = fechaPago(i);
      if (!fp) return false;
      if (fDesde && fp < fDesde) return false;
      if (fHasta && fp > fHasta) return false;
      return true;
    });
  }, [items, fDesde, fHasta]);

  const presetEsteMes = () => {
    const d = new Date();
    setFDesde(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
    setFHasta(new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10));
  };
  const presetMesAnterior = () => {
    const d = new Date();
    setFDesde(new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10));
    setFHasta(new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10));
  };
  const presetAnioActual = () => {
    const d = new Date();
    setFDesde(`${d.getFullYear()}-01-01`);
    setFHasta(`${d.getFullYear()}-12-31`);
  };

  const agrupados = useMemo(() => {
    const map = {};
    itemsFiltrados.forEach(i => {
      const k = i.proveedor_id || 'sin';
      if (!map[k]) map[k] = { nombre: i.proveedor_nombre, rut: i.proveedor_rut, items: [], total: 0 };
      map[k].items.push(i);
      map[k].total += Number(i.monto_clp || 0);
    });
    return Object.entries(map).map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [itemsFiltrados]);

  const totalFiltrado = useMemo(
    () => itemsFiltrados.reduce((acc, i) => acc + Number(i.monto_clp || 0), 0),
    [itemsFiltrados]
  );

  const subirComprobante = async (iid, file) => {
    setUploadingId(iid);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/gastos/items/${iid}/comprobante-pago`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprobante adjuntado');
      onReload();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
    finally { setUploadingId(null); }
  };

  const descargar = async (iid, filename) => {
    try {
      const resp = await api.get(`/gastos/items/${iid}/comprobante-pago`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'comprobante';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  return (
    <div className='space-y-3'>
      {/* Filtro fecha en Pagados */}
      <div className='flex items-center gap-2 flex-wrap p-3 bg-slate-50/60 border border-slate-200 rounded-md'
        data-testid='pagados-filtro-fecha'>
        <CalIcon className='w-4 h-4 text-slate-500' />
        <Label className='text-xs text-slate-600'>Pagados desde</Label>
        <Input type='date' value={fDesde} onChange={e => setFDesde(e.target.value)}
          className='h-8 w-40 text-xs bg-white' data-testid='pagados-fecha-desde' />
        <Label className='text-xs text-slate-600'>Hasta</Label>
        <Input type='date' value={fHasta} onChange={e => setFHasta(e.target.value)}
          className='h-8 w-40 text-xs bg-white' data-testid='pagados-fecha-hasta' />
        <Button size='sm' variant='ghost' onClick={() => { setFDesde(''); setFHasta(''); }}
          disabled={!fDesde && !fHasta}
          data-testid='pagados-fecha-limpiar'>Limpiar</Button>
        <Button size='sm' variant='outline' onClick={presetEsteMes}
          data-testid='pagados-fecha-mes-actual'>Mes actual</Button>
        <Button size='sm' variant='outline' onClick={presetMesAnterior}
          data-testid='pagados-fecha-mes-anterior'>Mes anterior</Button>
        <Button size='sm' variant='outline' onClick={presetAnioActual}
          data-testid='pagados-fecha-anio'>Año actual</Button>
        <div className='ml-auto flex items-center gap-2'>
          <Badge variant='outline' className='text-[10px]'>
            {itemsFiltrados.length} de {items.length}
          </Badge>
          {(fDesde || fHasta) && (
            <Badge className='text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300'
              data-testid='pagados-total-filtrado'>
              Total: {fmtCLP(totalFiltrado)}
            </Badge>
          )}
        </div>
      </div>

      <Card className='p-3 bg-amber-50 border-amber-200 text-xs text-amber-800'>
        <Lock className='w-3.5 h-3.5 inline mr-1' />
        <b>Pestaña inmutable</b>: los registros pagados no se pueden editar ni eliminar (auditoría).
      </Card>

      {agrupados.length === 0 ? (
        <Card className='p-12 text-center border-slate-200 border-dashed'>
          <Receipt className='w-12 h-12 text-slate-300 mx-auto mb-3' />
          <p className='text-slate-500'>
            {items.length === 0 ? 'Aún no hay gastos pagados.' : 'Sin pagos en el rango de fechas seleccionado.'}
          </p>
        </Card>
      ) : agrupados.map(g => (
        <Card key={g.id} className='border-slate-200 overflow-hidden' data-testid={`pagados-prov-${g.id}`}>
          <button onClick={() => toggle(g.id)}
            className='w-full p-3 flex items-center justify-between hover:bg-slate-50 text-left'>
            <div className='flex items-center gap-2'>
              {expandidos[g.id] ? <ChevronDown className='w-4 h-4' /> : <ChevronRight className='w-4 h-4' />}
              <Building2 className='w-4 h-4 text-slate-500' />
              <span className='font-semibold text-slate-800'>{g.nombre || 'Sin proveedor'}</span>
              <span className='text-xs text-slate-500 font-mono'>{g.rut}</span>
            </div>
            <div className='flex items-center gap-3 text-sm'>
              <Badge variant='secondary'>{g.items.length} pagos</Badge>
              <span className='font-bold text-slate-900'>{fmtCLP(g.total)}</span>
            </div>
          </button>
          {expandidos[g.id] && (
            <div className='border-t border-slate-100'>
              <table className='w-full text-sm'>
                <thead className='bg-slate-50 text-xs uppercase text-slate-600'>
                  <tr>
                    <th className='text-left px-4 py-2'>Concepto</th>
                    <th className='text-left px-4 py-2'>Comprobante</th>
                    <th className='text-left px-4 py-2'>Pagado</th>
                    <th className='text-right px-4 py-2'>Monto</th>
                    <th className='text-right px-4 py-2'>Cartola</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map(i => (
                    <tr key={i.id} className='border-t border-slate-100'>
                      <td className='px-4 py-2'>
                        {i.concepto}
                        {i.es_remuneracion && (
                          <Badge variant='outline' className='ml-2 text-[10px]'>Remuneraciones</Badge>
                        )}
                      </td>
                      <td className='px-4 py-2 text-xs text-slate-600 font-mono'>{i.comprobante}</td>
                      <td className='px-4 py-2 text-xs text-slate-600'>{fmtFecha(i.updated_at || i.created_at)}</td>
                      <td className='px-4 py-2 text-right font-semibold'>{fmtCLP(i.monto_clp)}</td>
                      <td className='px-4 py-2 text-right'>
                        {i.comprobante_pago ? (
                          <Button size='sm' variant='ghost'
                            onClick={() => descargar(i.id, i.comprobante_pago.filename)}>
                            <Download className='w-3.5 h-3.5' />
                          </Button>
                        ) : (
                          <label className='cursor-pointer inline-flex items-center text-xs text-blue-600 hover:underline'>
                            {uploadingId === i.id ? <Loader2 className='w-3.5 h-3.5 animate-spin' /> :
                              <><Upload className='w-3.5 h-3.5 mr-1' />Adjuntar</>}
                            <input type='file' className='hidden'
                              accept='.pdf,.jpg,.jpeg,.png,.webp'
                              onChange={e => e.target.files?.[0] && subirComprobante(i.id, e.target.files[0])} />
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ============= TABLA REUTILIZABLE =============
function ItemsTable({ items, seleccionados, toggleSel, toggleSelTodos, allSelected,
                     onEditar, onEliminar, mostrarAcciones, showCheckbox, showSemaforo }) {
  if (items.length === 0) {
    return (
      <Card className='p-16 text-center border-slate-200 border-dashed bg-slate-50/40'>
        <div className='inline-flex p-4 rounded-full bg-white shadow-sm mb-4 ring-1 ring-slate-200'>
          <FileSpreadsheet className='w-10 h-10 text-slate-300' />
        </div>
        <p className='text-slate-600 font-medium'>Sin gastos en esta sección</p>
        <p className='text-xs text-slate-400 mt-1'>Crea uno desde la pestaña Matriz para empezar.</p>
      </Card>
    );
  }
  return (
    <Card className='border-slate-200 overflow-hidden'>
      <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-slate-50 text-xs uppercase text-slate-600 tracking-wide'>
          <tr>
            {showCheckbox && (
              <th className='text-left px-3 py-3 w-10'>
                <Checkbox checked={allSelected} onCheckedChange={toggleSelTodos}
                  data-testid='gastos-sel-todos' />
              </th>
            )}
            {showSemaforo && <th className='text-left px-3 py-3 w-8'></th>}
            <th className='text-left px-4 py-3 font-semibold'>Vencimiento</th>
            <th className='text-left px-4 py-3 font-semibold'>Proveedor</th>
            <th className='text-left px-4 py-3 font-semibold'>Concepto</th>
            <th className='text-left px-4 py-3 font-semibold'>Comprobante</th>
            <th className='text-right px-4 py-3 font-semibold'>Monto</th>
            <th className='text-left px-4 py-3 font-semibold'>Asignación</th>
            {mostrarAcciones && <th className='text-right px-4 py-3'></th>}
          </tr>
        </thead>
        <tbody>
          {items.map(i => {
            const asig = i.asignacion || {};
            let asigStr = '—';
            if (asig.tipo === 'unica') asigStr = asig.area;
            else if (asig.tipo === 'todas') asigStr = 'Todas (equitativo)';
            else if (asig.tipo === 'seleccionadas') {
              asigStr = asig.distribucion?.length
                ? `${asig.distribucion.length} áreas (%)`
                : `${asig.areas?.length || 0} áreas`;
            }
            return (
              <tr key={i.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${i.es_remuneracion ? 'bg-violet-50/30' : ''}`}
                data-testid={`gastos-row-${i.id}`}>
                {showCheckbox && (
                  <td className='px-3 py-2'>
                    <Checkbox checked={!!seleccionados?.[i.id]} onCheckedChange={() => toggleSel(i.id)}
                      data-testid={`gastos-sel-${i.id}`} />
                  </td>
                )}
                {showSemaforo && (
                  <td className='px-3 py-2'>
                    <div className={`w-3 h-3 rounded-full ${i.semaforo === 'rojo' ? 'bg-red-500' : i.semaforo === 'amarillo' ? 'bg-amber-400' : 'bg-emerald-500'}`} />
                  </td>
                )}
                <td className='px-4 py-2 text-slate-700 whitespace-nowrap'>
                  {fmtFecha(i.fecha_vencimiento)}
                  {showSemaforo && i.semaforo === 'rojo' && (
                    <Badge className={`ml-2 text-[10px] ${semaforoColor.rojo}`}>VENCE</Badge>
                  )}
                </td>
                <td className='px-4 py-2 text-slate-800'>{i.proveedor_nombre}</td>
                <td className='px-4 py-2 text-slate-700'>
                  {i.concepto}
                  {i.es_remuneracion && (
                    <Badge variant='outline' className='ml-1 text-[10px] bg-violet-50'>
                      Nómina · {i.empleados?.length || 0} empleados
                    </Badge>
                  )}
                </td>
                <td className='px-4 py-2 text-xs text-slate-600 font-mono'>{i.comprobante}</td>
                <td className='px-4 py-2 text-right font-semibold text-slate-900'>{fmtCLP(i.monto_clp)}</td>
                <td className='px-4 py-2 text-xs text-slate-600'>{asigStr}</td>
                {mostrarAcciones && (
                  <td className='px-4 py-2 text-right whitespace-nowrap'>
                    {onEditar && (
                      <Button size='sm' variant='ghost' onClick={() => onEditar(i)}
                        data-testid={`gastos-edit-${i.id}`}>
                        <Edit className='w-3.5 h-3.5' />
                      </Button>
                    )}
                    {onEliminar && (
                      <Button size='sm' variant='ghost' onClick={() => onEliminar(i)}
                        data-testid={`gastos-del-${i.id}`}>
                        <Trash2 className='w-3.5 h-3.5 text-red-500' />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </Card>
  );
}

// ============= MODAL PROVEEDOR =============
function ProveedorModal({ proveedor, metadata, onClose, onSaved }) {
  const [nombre, setNombre] = useState(proveedor?.nombre || '');
  const [rut, setRut] = useState(proveedor?.rut || '');
  const [email, setEmail] = useState(proveedor?.email || '');
  const [bancoCodigo, setBancoCodigo] = useState(proveedor?.banco_codigo || '');
  const [tipoCuenta, setTipoCuenta] = useState(proveedor?.tipo_cuenta || 'Cuenta Vista');
  const [numeroCuenta, setNumeroCuenta] = useState(proveedor?.numero_cuenta || '');
  const [esTrabajador, setEsTrabajador] = useState(!!proveedor?.es_trabajador);
  const [saving, setSaving] = useState(false);

  const bancoNombre = useMemo(() => {
    const b = (metadata?.bancos_sbif || []).find(x => x.codigo === bancoCodigo);
    return b?.nombre || '';
  }, [bancoCodigo, metadata]);

  const submit = async () => {
    if (nombre.trim().length < 2 || !rut.trim()) { toast.error('Nombre y RUT requeridos'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        rut: rut.trim(),
        email: email.trim() || null,
        banco_codigo: bancoCodigo || null,
        banco_nombre: bancoNombre || null,
        tipo_cuenta: tipoCuenta,
        numero_cuenta: numeroCuenta.trim() || null,
        es_trabajador: esTrabajador,
      };
      if (proveedor) await api.put(`/gastos/proveedores/${proveedor.id}`, payload);
      else await api.post('/gastos/proveedores', payload);
      toast.success('Guardado');
      onSaved();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-xl' data-testid='modal-proveedor'>
        <DialogHeader>
          <DialogTitle>{proveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
        </DialogHeader>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div className='md:col-span-2'>
            <Label>Nombre / Razón Social *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)}
              data-testid='input-prov-nombre' />
          </div>
          <div>
            <Label>RUT *</Label>
            <Input value={rut} onChange={e => setRut(e.target.value)}
              placeholder='76.123.456-7' data-testid='input-prov-rut' />
          </div>
          <div>
            <Label>Email (opcional)</Label>
            <Input value={email} onChange={e => setEmail(e.target.value)}
              placeholder='contacto@empresa.cl' data-testid='input-prov-email' />
          </div>
          <div>
            <Label>Banco (SBIF)</Label>
            <Select value={bancoCodigo} onValueChange={setBancoCodigo}>
              <SelectTrigger data-testid='input-prov-banco'><SelectValue placeholder='—' /></SelectTrigger>
              <SelectContent>
                {(metadata?.bancos_sbif || []).map(b => (
                  <SelectItem key={b.codigo} value={b.codigo}>{b.codigo} - {b.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo Cuenta</Label>
            <Select value={tipoCuenta} onValueChange={setTipoCuenta}>
              <SelectTrigger data-testid='input-prov-tipo-cta'><SelectValue /></SelectTrigger>
              <SelectContent>
                {(metadata?.tipos_cuenta || ['Cuenta Vista', 'Cuenta Corriente', 'Cuenta Ahorro', 'Cuenta RUT']).map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='md:col-span-2'>
            <Label>Nº Cuenta</Label>
            <Input value={numeroCuenta} onChange={e => setNumeroCuenta(e.target.value)}
              placeholder='0-000-1234567-8' data-testid='input-prov-cta' />
          </div>
          <div className='md:col-span-2 flex items-center gap-2 pt-1'>
            <Checkbox checked={esTrabajador} onCheckedChange={setEsTrabajador}
              data-testid='input-prov-es-trabajador' />
            <Label className='text-sm cursor-pointer' onClick={() => setEsTrabajador(v => !v)}>
              Marcar como <b>Trabajador</b> de Peritum
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid='btn-prov-guardar'>
            {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= MODAL GASTO =============
function GastoModal({ gasto, proveedores, metadata, onClose, onSaved }) {
  const initAsig = gasto?.asignacion || { tipo: 'unica', area: metadata.areas[0] };
  const [proveedorId, setProveedorId] = useState(gasto?.proveedor_id || '');
  const [concepto, setConcepto] = useState(gasto?.concepto || '');
  const [comprobante, setComprobante] = useState(gasto?.comprobante || '');
  const [monto, setMonto] = useState(gasto?.monto_clp || '');
  const [venc, setVenc] = useState(gasto?.fecha_vencimiento || '');
  const [tipoAsig, setTipoAsig] = useState(initAsig.tipo || 'unica');
  const [areaUnica, setAreaUnica] = useState(initAsig.area || metadata.areas[0]);
  const [areasSel, setAreasSel] = useState(initAsig.areas || []);
  const [usaDistribucion, setUsaDistribucion] = useState(!!initAsig.distribucion);
  const [distribucion, setDistribucion] = useState(initAsig.distribucion || []);
  const [notas, setNotas] = useState(gasto?.notas || '');
  const [saving, setSaving] = useState(false);

  const toggleArea = (a) => {
    setAreasSel(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const submit = async () => {
    if (!proveedorId) { toast.error('Selecciona proveedor'); return; }
    if (!concepto.trim()) { toast.error('Concepto requerido'); return; }
    if (!comprobante.trim()) { toast.error('Comprobante requerido'); return; }
    if (!Number(monto) || Number(monto) <= 0) { toast.error('Monto inválido'); return; }
    if (!venc) { toast.error('Fecha vencimiento requerida'); return; }

    let asignacion;
    if (tipoAsig === 'unica') asignacion = { tipo: 'unica', area: areaUnica };
    else if (tipoAsig === 'todas') asignacion = { tipo: 'todas' };
    else {
      if (usaDistribucion) {
        const total = distribucion.reduce((acc, d) => acc + Number(d.porcentaje || 0), 0);
        if (Math.abs(total - 100) > 0.01) {
          toast.error(`Los porcentajes deben sumar 100% (actual ${total.toFixed(2)}%)`);
          return;
        }
        asignacion = { tipo: 'seleccionadas', distribucion };
      } else {
        if (areasSel.length === 0) { toast.error('Selecciona al menos un área'); return; }
        asignacion = { tipo: 'seleccionadas', areas: areasSel };
      }
    }
    const payload = {
      proveedor_id: proveedorId, concepto: concepto.trim(), comprobante: comprobante.trim(),
      monto_clp: Number(monto), fecha_vencimiento: venc, asignacion, notas: notas.trim()
    };
    setSaving(true);
    try {
      if (gasto) await api.put(`/gastos/items/${gasto.id}`, payload);
      else await api.post('/gastos/items', payload);
      toast.success('Guardado');
      onSaved();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto' data-testid='modal-gasto'>
        <DialogHeader>
          <DialogTitle>{gasto ? 'Editar Gasto' : 'Nuevo Gasto'}</DialogTitle>
        </DialogHeader>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div className='md:col-span-2'>
            <Label>Proveedor *</Label>
            <Select value={proveedorId} onValueChange={setProveedorId}>
              <SelectTrigger data-testid='input-gasto-proveedor'><SelectValue placeholder='Selecciona proveedor' /></SelectTrigger>
              <SelectContent>
                {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre} — {p.rut}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className='md:col-span-2'>
            <Label>Concepto / Glosa *</Label>
            <Input value={concepto} onChange={e => setConcepto(e.target.value)}
              data-testid='input-gasto-concepto' />
          </div>
          <div>
            <Label>Nº Comprobante *</Label>
            <Input value={comprobante} onChange={e => setComprobante(e.target.value)}
              data-testid='input-gasto-comprobante' />
          </div>
          <div>
            <Label>Monto CLP *</Label>
            <Input type='number' value={monto} onChange={e => setMonto(e.target.value)}
              data-testid='input-gasto-monto' />
          </div>
          <div>
            <Label>Vencimiento *</Label>
            <Input type='date' value={venc} onChange={e => setVenc(e.target.value)}
              data-testid='input-gasto-venc' />
          </div>
          <div className='md:col-span-2 pt-2 border-t border-slate-100'>
            <Label className='text-sm font-semibold'>Centro de Costo</Label>
            <div className='flex gap-2 mt-1 flex-wrap'>
              {['unica', 'todas', 'seleccionadas'].map(t => (
                <Button key={t} variant={tipoAsig === t ? 'default' : 'outline'} size='sm'
                  onClick={() => setTipoAsig(t)} data-testid={`asig-tipo-${t}`}>
                  {t === 'unica' ? 'Área única' : t === 'todas' ? 'Todas (equitativo)' : 'Seleccionar áreas'}
                </Button>
              ))}
            </div>
          </div>
          {tipoAsig === 'unica' && (
            <div className='md:col-span-2'>
              <Label>Área asignada</Label>
              <Select value={areaUnica} onValueChange={setAreaUnica}>
                <SelectTrigger data-testid='input-area-unica'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {metadata.areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {tipoAsig === 'todas' && (
            <p className='md:col-span-2 text-xs text-slate-500 italic'>
              El monto se divide en partes iguales entre: {metadata.areas.join(', ')}.
            </p>
          )}
          {tipoAsig === 'seleccionadas' && (
            <div className='md:col-span-2 space-y-2'>
              <div className='flex items-center gap-2'>
                <Checkbox checked={usaDistribucion} onCheckedChange={(v) => {
                  setUsaDistribucion(v);
                  if (v && distribucion.length === 0) {
                    setDistribucion(areasSel.map(a => ({ area: a, porcentaje: Math.round(100 / Math.max(areasSel.length, 1)) })));
                  }
                }} data-testid='asig-toggle-pct' />
                <Label className='text-xs'>Definir porcentajes manuales</Label>
              </div>
              {!usaDistribucion ? (
                <div className='grid grid-cols-2 gap-1'>
                  {metadata.areas.map(a => (
                    <label key={a} className='flex items-center gap-2 text-sm'>
                      <Checkbox checked={areasSel.includes(a)} onCheckedChange={() => toggleArea(a)}
                        data-testid={`asig-area-${a}`} />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className='space-y-1'>
                  {metadata.areas.map(a => {
                    const d = distribucion.find(x => x.area === a);
                    return (
                      <div key={a} className='flex items-center gap-2'>
                        <span className='text-xs flex-1'>{a}</span>
                        <Input type='number' className='w-24'
                          value={d?.porcentaje || 0}
                          onChange={e => {
                            const v = Number(e.target.value) || 0;
                            const next = distribucion.filter(x => x.area !== a);
                            if (v > 0) next.push({ area: a, porcentaje: v });
                            setDistribucion(next);
                          }} />
                        <span className='text-xs text-slate-500'>%</span>
                      </div>
                    );
                  })}
                  <p className='text-[10px] text-slate-500'>
                    Suma actual: {distribucion.reduce((acc, d) => acc + Number(d.porcentaje || 0), 0).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          )}
          <div className='md:col-span-2'>
            <Label>Notas (opcional)</Label>
            <Textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
              data-testid='input-gasto-notas' />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid='btn-gasto-guardar'>
            {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= MODAL REMUNERACIONES =============
function RemuneracionesModal({ proveedores, metadata, onClose, onSaved }) {
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [proveedorId, setProveedorId] = useState('');
  const [vencimiento, setVencimiento] = useState('');
  const [empleados, setEmpleados] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notas, setNotas] = useState('');

  // Precargar empleados del ERP
  useEffect(() => {
    (async () => {
      setLoadingEmp(true);
      try {
        const { data } = await api.get('/gastos/empleados-erp');
        setEmpleados((data.items || []).map(e => ({ ...e, sueldo_clp: 0 })));
      } catch (e) {
        toast.error('Error cargando empleados');
      } finally { setLoadingEmp(false); }
    })();
  }, []);

  const replicarMesAnterior = async () => {
    if (!mes) return;
    try {
      const { data } = await api.get('/gastos/remuneraciones/mes-anterior', { params: { mes } });
      if (!data.empleados?.length) {
        toast.info('No hay nómina previa para replicar');
        return;
      }
      setEmpleados(emps => emps.map(e => {
        const prev = data.empleados.find(p => (p.user_id && p.user_id === e.user_id) || (p.rut && p.rut === e.rut));
        return prev ? { ...e, ...prev } : e;
      }));
      toast.success(`Datos replicados desde ${data.mes_origen}`);
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  const cargarPlanillaEstandar = async () => {
    try {
      const { data } = await api.get('/gastos/remuneraciones/planilla-estandar');
      if (!data.items?.length) { toast.info('No hay trabajadores marcados con es_trabajador=true'); return; }
      setEmpleados(data.items.map(e => ({
        user_id: e.user_id,
        nombre: e.nombre,
        email: e.email,
        rut: e.rut,
        banco_codigo: e.banco_codigo,
        banco_nombre: e.banco_nombre,
        numero_cuenta: e.numero_cuenta,
        tipo_cuenta: e.tipo_cuenta,
        area: e.area,
        es_socio: e.es_socio,
        sueldo_clp: e.sueldo_clp,
      })));
      const msg = `Planilla estándar: ${data.total_trabajadores} trabajadores · ${data.incompletos} sin datos completos`;
      if (data.meses_promediados > 0) {
        toast.success(`${msg} · sueldos promediados de ${data.meses_origen.join(', ')}`);
      } else {
        toast.warning(`${msg} · sin histórico (sueldos en 0, completa manualmente)`);
      }
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
  };

  const updateEmp = (i, field, value) => {
    setEmpleados(arr => arr.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  };

  const totalPlanilla = useMemo(
    () => empleados.reduce((acc, e) => acc + Number(e.sueldo_clp || 0), 0), [empleados]
  );
  const conSueldo = empleados.filter(e => Number(e.sueldo_clp) > 0);

  const submit = async () => {
    if (!proveedorId) { toast.error('Selecciona proveedor'); return; }
    if (!vencimiento) { toast.error('Vencimiento requerido'); return; }
    if (conSueldo.length === 0) { toast.error('Asigna sueldo a al menos un empleado'); return; }
    // Validaciones bancarias
    for (const e of conSueldo) {
      if (!e.rut) { toast.error(`Falta RUT de ${e.nombre}`); return; }
      if (!e.banco_codigo) { toast.error(`Falta banco SBIF de ${e.nombre}`); return; }
      if (!e.numero_cuenta) { toast.error(`Falta Nº cuenta de ${e.nombre}`); return; }
    }
    setSaving(true);
    try {
      await api.post('/gastos/remuneraciones', {
        mes,
        proveedor_id: proveedorId,
        fecha_vencimiento: vencimiento,
        empleados: conSueldo.map(e => ({
          user_id: e.user_id, nombre: e.nombre, rut: e.rut,
          banco_codigo: e.banco_codigo, numero_cuenta: e.numero_cuenta,
          tipo_cuenta: e.tipo_cuenta, area: e.area, sueldo_clp: Number(e.sueldo_clp)
        })),
        notas: notas.trim()
      });
      toast.success(`Planilla ${mes} creada con ${conSueldo.length} empleados`);
      onSaved();
    } catch (e) { toast.error('Error: ' + formatErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-6xl max-h-[90vh] overflow-y-auto' data-testid='modal-remuneraciones'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <UsersIcon className='w-5 h-5 text-primary' /> Cargar Planilla de Remuneraciones
          </DialogTitle>
          <DialogDescription>
            Define el mes, el proveedor pagador, y los sueldos líquidos por empleado. Empleados con sueldo 0 no entrarán a la planilla.
          </DialogDescription>
        </DialogHeader>

        <div className='grid grid-cols-1 md:grid-cols-4 gap-3 sticky top-0 bg-white py-2 z-10 border-b border-slate-100'>
          <div>
            <Label>Mes (YYYY-MM)</Label>
            <Input type='month' value={mes} onChange={e => setMes(e.target.value)}
              data-testid='input-rem-mes' />
          </div>
          <div>
            <Label>Proveedor pagador</Label>
            <Select value={proveedorId} onValueChange={setProveedorId}>
              <SelectTrigger data-testid='input-rem-proveedor'><SelectValue placeholder='Selecciona' /></SelectTrigger>
              <SelectContent>
                {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vencimiento</Label>
            <Input type='date' value={vencimiento} onChange={e => setVencimiento(e.target.value)}
              data-testid='input-rem-venc' />
          </div>
          <div className='flex items-end gap-1'>
            <Button variant='default' size='sm' onClick={cargarPlanillaEstandar}
              data-testid='btn-rem-estandar'>
              <UsersIcon className='w-3.5 h-3.5 mr-1' /> Planilla Estándar
            </Button>
            <Button variant='outline' size='sm' onClick={replicarMesAnterior}
              data-testid='btn-rem-replicar'>
              <Copy className='w-3.5 h-3.5 mr-1' /> Mes Anterior
            </Button>
          </div>
        </div>

        <div className='mt-3'>
          {loadingEmp ? (
            <div className='flex justify-center py-8'><Loader2 className='w-6 h-6 animate-spin' /></div>
          ) : (
            <Card className='border-slate-200 overflow-x-auto'>
              <table className='w-full text-xs'>
                <thead className='bg-slate-50 uppercase text-slate-600'>
                  <tr>
                    <th className='text-left px-2 py-2'>Empleado</th>
                    <th className='text-left px-2 py-2'>Área</th>
                    <th className='text-left px-2 py-2'>RUT</th>
                    <th className='text-left px-2 py-2'>Banco SBIF</th>
                    <th className='text-left px-2 py-2'>Tipo Cuenta</th>
                    <th className='text-left px-2 py-2'>Nº Cuenta</th>
                    <th className='text-right px-2 py-2'>Sueldo Líquido</th>
                  </tr>
                </thead>
                <tbody>
                  {empleados.map((e, i) => (
                    <tr key={e.user_id || i} className='border-t border-slate-100'
                      data-testid={`rem-row-${i}`}>
                      <td className='px-2 py-1.5'>
                        <div className='font-medium text-slate-800'>{e.nombre}</div>
                        <div className='text-[10px] text-slate-400'>{e.email}</div>
                      </td>
                      <td className='px-2 py-1.5 text-slate-600'>
                        {e.es_socio ? <Badge variant='outline' className='text-[10px]'>Socio</Badge> : e.area || '—'}
                      </td>
                      <td className='px-2 py-1.5'>
                        <Input className='h-7 text-xs' value={e.rut || ''}
                          onChange={ev => updateEmp(i, 'rut', ev.target.value)}
                          placeholder='11.111.111-1' />
                      </td>
                      <td className='px-2 py-1.5'>
                        <Select value={e.banco_codigo || ''} onValueChange={(v) => updateEmp(i, 'banco_codigo', v)}>
                          <SelectTrigger className='h-7 text-xs'><SelectValue placeholder='—' /></SelectTrigger>
                          <SelectContent>
                            {metadata.bancos_sbif.map(b => <SelectItem key={b.codigo} value={b.codigo}>{b.codigo} - {b.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className='px-2 py-1.5'>
                        <Select value={e.tipo_cuenta || 'Cuenta Vista'} onValueChange={(v) => updateEmp(i, 'tipo_cuenta', v)}>
                          <SelectTrigger className='h-7 text-xs'><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {metadata.tipos_cuenta.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className='px-2 py-1.5'>
                        <Input className='h-7 text-xs' value={e.numero_cuenta || ''}
                          onChange={ev => updateEmp(i, 'numero_cuenta', ev.target.value)} />
                      </td>
                      <td className='px-2 py-1.5'>
                        <Input type='number' className='h-7 text-xs text-right'
                          value={e.sueldo_clp || ''}
                          onChange={ev => updateEmp(i, 'sueldo_clp', ev.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className='bg-slate-50 sticky bottom-0'>
                  <tr>
                    <td colSpan={6} className='text-right px-2 py-2 text-xs font-semibold'>
                      Total planilla ({conSueldo.length} empleados):
                    </td>
                    <td className='text-right px-2 py-2 font-bold text-slate-900' data-testid='rem-total'>
                      {fmtCLP(totalPlanilla)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}
        </div>

        <div>
          <Label>Notas (opcional)</Label>
          <Textarea rows={2} value={notas} onChange={e => setNotas(e.target.value)} />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || conSueldo.length === 0}
            data-testid='btn-rem-guardar'>
            {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : `Cargar Planilla (${conSueldo.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
